import { useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { KeyRound, Mail, Copy, CheckCircle2, AlertCircle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

/**
 * BulkPasswordDialog — R112.
 *
 * Two bulk actions:
 *   - Email reset links: fires POST /admin/users/bulk-password-reset for
 *     the selected user_ids. Per-user delivered/queued/error surfaces in
 *     a results panel so admins see who to chase manually.
 *   - Set shared temp password: fires POST /admin/users/bulk-set-password
 *     with a single password + force_change_on_login. Admins hand out
 *     the same temp password to a whole batch (onboarding day).
 */

function generatePassword(len = 14) {
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
  const symbols = "!@#$%&*";
  let out = "";
  for (let i = 0; i < len - 1; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  out += symbols[Math.floor(Math.random() * symbols.length)];
  return out;
}

export function BulkPasswordDialog({ open, onClose, selectedUsers }) {
  const [mode, setMode] = useState("link");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [forceChange, setForceChange] = useState(true);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const count = selectedUsers.length;
  const passwordValid = password.length >= 10
    && /[a-zA-Z]/.test(password) && /[0-9]/.test(password);

  const suggest = () => { setPassword(generatePassword()); setReveal(true); };

  const submit = async () => {
    if (count === 0) return;
    setBusy(true);
    try {
      if (mode === "direct") {
        if (!passwordValid) {
          toast.error("Password needs 10+ chars, one letter, one digit");
          setBusy(false);
          return;
        }
        const { data } = await api.post("/admin/users/bulk-set-password", {
          user_ids: selectedUsers.map((u) => u.id),
          password,
          force_change_on_login: forceChange,
        });
        setResults({ mode, ...data });
        toast.success(`Password set for ${data.applied} of ${data.attempted} users`, {
          duration: 8000,
          description: forceChange ? "They'll be prompted to change on next login." : "Hand out the password out-of-band.",
          action: {
            label: "Copy password",
            onClick: () => { navigator.clipboard.writeText(password); toast.info("Copied"); },
          },
        });
      } else {
        const { data } = await api.post("/admin/users/bulk-password-reset", {
          user_ids: selectedUsers.map((u) => u.id),
        });
        setResults({ mode, ...data });
        const summary = `${data.delivered} delivered · ${data.failed} failed`;
        if (data.failed === 0) toast.success(`Reset links sent — ${summary}`);
        else toast.warning(`Bulk send partial — ${summary}`, { duration: 8000,
          description: "Check the results panel to copy fallback links for failed sends." });
      }
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Bulk action failed");
    } finally { setBusy(false); }
  };

  const closeAndReset = (reload = false) => {
    setResults(null);
    setPassword("");
    setMode("link");
    onClose(reload);
  };

  const copyAllLinks = () => {
    if (!results?.results) return;
    const lines = results.results
      .filter((r) => r.reset_link)
      .map((r) => `${r.email || r.user_id}: ${r.reset_link}`)
      .join("\n");
    if (!lines) { toast.info("No links to copy"); return; }
    navigator.clipboard.writeText(lines);
    toast.success(`${lines.split("\n").length} link(s) copied to clipboard`);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && closeAndReset(false)}>
      <DialogContent className="bg-card border-white/10 max-w-lg" data-testid="bulk-pw-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-orange-400" />
            Bulk password action · {count} user{count === 1 ? "" : "s"}
          </DialogTitle>
          <DialogDescription>
            Applies to all selected users. Use the results below to copy fallback links if email delivery fails.
          </DialogDescription>
        </DialogHeader>

        {!results && (
          <>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <button
                type="button"
                data-testid="bulk-pw-mode-link"
                onClick={() => setMode("link")}
                className={`p-3 rounded-lg border text-left transition ${
                  mode === "link" ? "border-orange-500/40 bg-orange-500/10"
                                  : "border-white/10 hover:bg-white/[0.03]"
                }`}>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Mail className="h-3.5 w-3.5 text-orange-300" /> Email reset links
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Each user gets their own magic link.
                </div>
              </button>
              <button
                type="button"
                data-testid="bulk-pw-mode-direct"
                onClick={() => setMode("direct")}
                className={`p-3 rounded-lg border text-left transition ${
                  mode === "direct" ? "border-orange-500/40 bg-orange-500/10"
                                    : "border-white/10 hover:bg-white/[0.03]"
                }`}>
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <KeyRound className="h-3.5 w-3.5 text-orange-300" /> Set shared temp password
                </div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  Same temp password for everyone. Admin accounts are refused for safety.
                </div>
              </button>
            </div>

            {mode === "direct" && (
              <div className="mt-4 space-y-3" data-testid="bulk-pw-direct-block">
                <div>
                  <Label>Temporary password</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="relative flex-1">
                      <Input
                        data-testid="bulk-pw-input"
                        type={reveal ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="10+ chars with a letter + digit"
                        className="bg-white/[0.04] border-white/10 pr-9"
                        autoComplete="new-password"
                      />
                      <button type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                              onClick={() => setReveal(!reveal)}>
                        {reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                    </div>
                    <Button type="button" variant="outline" className="border-white/10 shrink-0"
                            onClick={suggest} data-testid="bulk-pw-suggest">Generate</Button>
                    {password && (
                      <Button type="button" variant="outline" size="icon" className="border-white/10 shrink-0"
                              onClick={() => { navigator.clipboard.writeText(password); toast.info("Copied"); }}
                              data-testid="bulk-pw-copy">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                  {password && !passwordValid && (
                    <div className="text-[11px] text-rose-300 mt-1">
                      Password needs at least one letter and one digit.
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-white/[0.02] border border-white/10">
                  <div>
                    <div className="text-xs font-medium">Force change on next login</div>
                    <div className="text-[11px] text-zinc-500">Prompt them to pick their own on first sign-in.</div>
                  </div>
                  <Switch checked={forceChange} onCheckedChange={setForceChange} data-testid="bulk-pw-force-change" />
                </div>
              </div>
            )}

            {mode === "link" && (
              <div className="mt-4 text-xs text-zinc-400 p-3 rounded bg-white/[0.02] border border-white/10">
                A reset email will be sent to each selected user. If email delivery fails,
                the per-user results panel will offer fallback links you can copy and share manually.
              </div>
            )}

            <div className="mt-3 text-[11px] text-zinc-500">
              Selected: {selectedUsers.slice(0, 3).map((u) => u.name).join(", ")}
              {selectedUsers.length > 3 && ` and ${selectedUsers.length - 3} more`}
            </div>
          </>
        )}

        {results && (
          <div className="mt-2" data-testid="bulk-pw-results">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-sm">
                {results.mode === "direct"
                  ? <>Applied to <b className="text-emerald-300">{results.applied}</b> of {results.attempted} users</>
                  : <><b className="text-emerald-300">{results.delivered}</b> delivered · <b className="text-rose-300">{results.failed}</b> failed</>}
              </div>
              {results.mode === "link" && results.failed > 0 && (
                <Button size="sm" variant="outline" className="ml-auto border-orange-500/30 text-orange-300 h-7 text-[11px]"
                        onClick={copyAllLinks} data-testid="bulk-pw-copy-links">
                  <Copy className="h-3 w-3 mr-1" /> Copy all fallback links
                </Button>
              )}
            </div>
            <div className="max-h-64 overflow-y-auto rounded border border-white/10 divide-y divide-white/5">
              {results.results.map((r) => (
                <div key={r.user_id} className="flex items-start gap-2 p-2 text-[11px]" data-testid={`bulk-pw-result-${r.user_id}`}>
                  {r.ok ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400 mt-0.5" />
                        : <AlertCircle className="h-3.5 w-3.5 text-rose-400 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className={r.ok ? "text-zinc-200" : "text-rose-300"}>
                      {r.name || r.email || r.user_id}
                      <span className="text-zinc-500 ml-1">({r.role || "?"})</span>
                    </div>
                    {!r.ok && <div className="text-[10px] text-zinc-500">— {r.reason || r.error || "failed"}</div>}
                    {results.mode === "link" && r.reset_link && r.delivered === false && (
                      <button className="text-[10px] text-orange-300 hover:text-orange-200 underline mt-1"
                              onClick={() => { navigator.clipboard.writeText(r.reset_link); toast.info("Link copied"); }}>
                        Copy fallback link
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => closeAndReset(true)} className="border-white/10"
                  data-testid="bulk-pw-close">
            {results ? "Done" : "Cancel"}
          </Button>
          {!results && (
            <Button
              onClick={submit}
              disabled={busy || count === 0 || (mode === "direct" && !passwordValid)}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="bulk-pw-submit">
              {busy ? "Working…"
                : mode === "direct" ? `Set password for ${count} user${count === 1 ? "" : "s"}`
                : `Send reset link to ${count} user${count === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
